import { createFileRoute, Link } from "@tanstack/react-router";

import { Footer } from "@/components/footer";
import { Testimonials } from "@/components/testimonials";
import { useRef } from "react";

import { Navbar } from "@/components/navbar";
import { Pricing } from "@/components/pricing";
import { ReportShowcase } from "@/components/ReportShowcase";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronRight } from "lucide-react";

export const Route = createFileRoute("/")({
  component: Index,
});

function Index() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.8]);

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const variant = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <>
      <Navbar />
      <div className="font-sans text-slate-800 bg-white selection:bg-violet-200 selection:text-violet-900 overflow-x-hidden">
        {/* Header */}

        {/* Hero Section */}
        <section
          ref={heroRef}
          className="relative py-32 md:pt-40 overflow-hidden"
        >
          <div className="absolute inset-0 -z-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(124,58,237,0.12),transparent_50%)]"></div>
            <div className="absolute bottom-0 left-0 right-0 h-[400px] bg-[radial-gradient(circle_at_bottom_left,rgba(124,58,237,0.08),transparent_50%)]"></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[conic-gradient(from_90deg_at_50%_50%,rgba(124,58,237,0.05)_0deg,rgba(124,58,237,0.1)_90deg,rgba(124,58,237,0.05)_180deg,rgba(124,58,237,0.1)_270deg,rgba(124,58,237,0.05)_360deg)] blur-[100px] opacity-30"></div>
          </div>

          <motion.div
            style={{ opacity, scale }}
            className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="max-w-4xl mx-auto"
            >
              <div className="inline-flex items-center px-3 py-1 mb-8 text-sm font-medium text-violet-800 bg-violet-100 rounded-full">
                <span className="flex h-2 w-2 rounded-full bg-violet-500 mr-2"></span>
                New: AI-powered creative insights
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-slate-900 mb-6 leading-[1.1] text-balance">
                Protect Your Media Budget:
                <br />
                Test Creative Before You Spend
              </h1>
              <p className="text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto mb-10 leading-relaxed">
                Get immediate, data-driven feedback on your ads, landing pages,
                and marketing assets from AI Twins representing your actual
                consumers—before risking a single dollar on media.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button
                  size="lg"
                  className="text-base px-8 py-6 bg-violet-600 hover:bg-violet-700 rounded-full shadow-lg shadow-violet-200/50 hover:shadow-violet-300/50 transition-all duration-300"
                  asChild
                >
                  <Link to="/get-started">
                    Start Testing Your Creative
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="text-base px-8 py-6 rounded-full border-slate-300 hover:bg-slate-100 transition-all duration-300"
                  onClick={() => {
                    const demoSection = document.getElementById("demo");
                    if (demoSection) {
                      demoSection.scrollIntoView({ behavior: "smooth" });
                    }
                  }}
                >
                  See Sample Report
                  {/* <Play className="ml-2 h-4 w-4" /> */}
                </Button>
              </div>
            </motion.div>

            {/* <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-16 max-w-5xl mx-auto"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <div className="absolute inset-0 bg-gradient-to-r from-violet-600/20 to-fuchsia-600/20 mix-blend-overlay"></div>
                <div className="aspect-video bg-gradient-to-r from-violet-900 to-fuchsia-900 flex items-center justify-center">
                  <img
                    src="/placeholder.svg?height=720&width=1280"
                    alt="Platform preview"
                    width={1280}
                    height={720}
                    className="w-full h-auto mix-blend-overlay opacity-90"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <Play className="h-5 w-5 text-white" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium">See how it works</p>
                      <p className="text-xs opacity-80">2:45 min</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div> */}
          </motion.div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 md:py-16" id="how-it-works">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="inline-flex items-center px-3 py-1 mb-4 text-sm font-medium text-violet-800 bg-violet-100 rounded-full">
                The Process
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                How Creative Evaluation Works
              </h2>
              <p className="text-lg text-slate-600">
                Powered by Real Consumer Intelligence
              </p>
            </div>

            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-100px" }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            >
              {[
                {
                  step: 1,
                  title: "Update User Details",
                  description:
                    "Provide your contact information so we can send the comprehensive evaluation report directly to you.",
                },
                {
                  step: 2,
                  title: "Upload Creative ",
                  description:
                    "Share your brand URL along with the creative assets (image, video, or landing page) you want to evaluate.",
                },
                {
                  step: 3,
                  title: "Make Payment",
                  description:
                    "Complete secure payment for the comprehensive creative evaluation.",
                },
                {
                  step: 4,
                  title: "Receive Analysis in Email",
                  description:
                    "Get your detailed creative evaluation report delivered to your inbox within 24 hours, containing actionable insights from AI Twins.",
                },
              ].map((item, index) => (
                <motion.div key={index} variants={variant} className="relative">
                  <Card className="h-full overflow-visible bg-white/50 backdrop-blur-sm border border-slate-200/60 hover:border-violet-200 transition-all duration-300 hover:shadow-lg hover:shadow-violet-100/50 group py-3">
                    <div className="absolute -top-5 -left-5 bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white w-12 h-12 rounded-2xl flex items-center justify-center font-bold shadow-lg shadow-violet-200/50 rotate-0 group-hover:rotate-6 transition-transform duration-300">
                      {item.step}
                    </div>
                    <CardContent className="pt-8 pb-6 px-6">
                      <h3 className="text-xl font-semibold mb-4 text-slate-900 group-hover:text-violet-700 transition-colors duration-300">
                        {item.title}
                      </h3>
                      <p className="text-slate-600">{item.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Sample Section */}

        <ReportShowcase />

        {/* Testimonials Section */}
        {/* <section className="py-20 relative" id="testimonials">
          <div className="absolute inset-0 -z-10">
            <div className="absolute right-0 top-0 h-[500px] w-[500px] bg-[radial-gradient(circle_at_center,rgba(124,58,237,0.08),transparent_50%)]"></div>
          </div>
        </section> */}
        <Testimonials />

        {/* Pricing Section */}
        <Pricing />

        {/* What Makes Different Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl mx-auto text-center"
            >
              <div className="inline-flex items-center px-3 py-1 mb-4 text-sm font-medium text-violet-800 bg-violet-100 rounded-full">
                Our Difference
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8">
                What Makes it Different
              </h2>
              <div className="space-y-6 text-lg text-slate-600">
                <p>
                  Unlike other testing solutions, consumr.ai's Creative
                  Evaluation uses AI Twins built from actual behavioral data,
                  search patterns, and social conversations—never synthetic or
                  simulated data.
                </p>
                <p>
                  This means the feedback you receive accurately predicts how
                  real consumers will respond to your creative in the
                  marketplace.
                </p>
                <p>
                  By testing your creative before launching media campaigns, you
                  can identify and fix potential issues, strengthen your
                  messaging, and ensure your media budget delivers maximum
                  impact.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* About Section */}
        <section className="py-20 relative" id="about">
          <div className="absolute inset-0 -z-10">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(124,58,237,0.08),transparent_70%)]"></div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl mx-auto text-center"
            >
              <div className="inline-flex items-center px-3 py-1 mb-4 text-sm font-medium text-violet-800 bg-violet-100 rounded-full">
                About Us
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8">
                About consumr.ai
              </h2>
              <div className="space-y-6 text-lg text-slate-600">
                <p>
                  <a
                    href="https://consumr.ai"
                    target="_blank"
                    className="text-violet-600 hover:text-violet-700 transition-colors duration-300 underline underline-offset-2"
                  >
                    consumr.ai
                  </a>{" "}
                  is a consumer intelligence platform that analyzes data from
                  various publicly accessible user platforms to provide insights
                  for brands and agencies, enabling them to understand shifts in
                  the consumer landscape.
                </p>
                <p>
                  Utilizing proprietary technology, consumr.ai accesses near
                  real-time, observed and deterministic data from millions of
                  users worldwide and makes it comprehensible through
                  sophisticated algorithms that interpret and simplify numerous
                  data points.
                </p>
                <p>
                  Our platform makes data actionable by assisting users in
                  developing various strategies including media strategies,
                  content strategies, creative strategies, product strategies,
                  and general business strategies.
                </p>
              </div>
              <Button
                size="lg"
                className="mt-10 bg-violet-600 hover:bg-violet-700 rounded-full shadow-lg shadow-violet-200/50 hover:shadow-violet-300/50 transition-all duration-300"
                onClick={() => {
                  window.open("https://consumr.ai", "_blank");
                }}
              >
                Learn More About consumr.ai
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 relative overflow-hidden bg-gradient-to-br from-violet-600 to-fuchsia-600">
          <div className="absolute inset-0 bg-gradient-to-br from-violet-600 to-fuchsia-600 -z-10"></div>
          <div className="absolute inset-0 bg-[url('/placeholder.svg?height=500&width=500')] bg-center opacity-10 mix-blend-overlay -z-10"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.1),transparent_50%)] -z-10"></div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl mx-auto"
            >
              <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-white">
                Ready to Optimize Your Creative?
              </h2>
              <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto leading-relaxed">
                Start testing your ads, landing pages, and marketing assets
                today and make every dollar of your media budget count.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button
                  size="lg"
                  variant="secondary"
                  className="text-base px-8 py-6 bg-white text-violet-700 hover:bg-white/90 rounded-full shadow-lg shadow-violet-900/20 hover:shadow-violet-900/30 transition-all duration-300"
                  asChild
                >
                  <Link to="/contact-us">Contact Us</Link>

                  {/* <ArrowUpRight className="ml-2 h-4 w-4" /> */}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="text-base px-8 py-6 bg-transparent text-white border-white hover:bg-white/10 rounded-full transition-all duration-300"
                >
                  <Link to="/get-started">Get Started</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}

export default Index;
